/* ============================================================
   acg-free-gift-auto.js
   Automatic free-gift line for the ACG custom theme.

   Promo: buying a qualifying foam cannon / foam blaster earns a free
   Quan Pink Car Soap 32 oz. The $0 pricing is handled entirely by the
   three automatic Buy-X-Get-Y discounts already configured in the admin
   (FOAMCANNONQUANPINK, FOAMCANNONQUANPINK2, FOAMBLASTERQUANPINK) — this
   script only makes sure the gift is actually IN the cart so those
   discounts have something to apply to.

   Configured by snippets/acg-free-gift-auto.liquid, which sets
   window.ACGFreeGiftAutoConfig before this file loads. Variant IDs live
   there, not here.

   Behaviour:
     - reconcile() reads /cart.js and makes the cart match the promo:
       one free gift per qualifying unit, minus any of the same variant
       the customer is already paying for.
     - The gift line carries a hidden `_free_gift` property. That marker
       is the only thing this script will ever add, change, or remove —
       a Quan Pink the customer chose to buy is never touched.
     - If the customer removes the gift themselves, it stays gone for the
       rest of the session (they are not forced to keep it). Emptying the
       cart of qualifying items clears that opt-out.
     - reconcile() runs on page load, on `acg:cart:added`, and after any
       fetch/XHR to a /cart mutation endpoint — so every add-to-cart path
       in the theme is covered, including the ones that don't emit the
       ACG events (Dawn's product-form.js, app scripts, the /cart page).

   Internal cart requests use the fetch reference captured before the
   hook is installed, so the script can never re-trigger itself.
   ============================================================ */
(function () {
  'use strict';

  if (window.ACGFreeGiftAuto && window.ACGFreeGiftAuto.initialised) {
    return;
  }

  var CFG = window.ACGFreeGiftAutoConfig || {};

  var GIFT_ID = parseInt(CFG.giftVariantId, 10);
  var QUALIFYING = (CFG.qualifyingVariantIds || []).map(Number).filter(Boolean);
  var MARKER_KEY = CFG.markerKey || '_free_gift';
  var MARKER_VALUE = CFG.markerValue || 'Free gift';
  // Safety net: a runaway cart should never be able to request hundreds of
  // free units, however many qualifying items somehow ended up in it.
  var MAX_GIFTS = parseInt(CFG.maxGifts, 10) || 10;

  // A missing or half-written config is a reason to do nothing at all,
  // never a reason to guess at variant IDs.
  if (!GIFT_ID || !QUALIFYING.length) {
    return;
  }

  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  if (!nativeFetch) {
    return;
  }

  var SS_OPTOUT = 'acg_free_gift_optout';
  var SS_EXPECTED = 'acg_free_gift_expected';
  var SS_RELOADS = 'acg_free_gift_reloads';
  var SS_UNAVAILABLE = 'acg_free_gift_unavailable';
  var SS_CAP = 'acg_free_gift_cap';
  var MAX_RELOADS = 2;
  var DEBOUNCE_MS = 200;

  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  var busy = false;
  var queued = false;
  var debounceTimer = null;

  /* ---- Session storage (may be unavailable in private mode) -------- */

  function ssGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function ssSet(key, value) {
    try {
      if (value === null) {
        window.sessionStorage.removeItem(key);
      } else {
        window.sessionStorage.setItem(key, value);
      }
    } catch (e) {
      /* storage blocked — the promo still works, just less sticky */
    }
  }

  function ssNum(key) {
    var raw = ssGet(key);
    if (raw === null || raw === '') {
      return null;
    }
    var n = parseInt(raw, 10);
    return isNaN(n) ? null : n;
  }

  /* ---- Cart requests ---------------------------------------------- */

  function route(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + path;
  }

  function asJSON(response) {
    return response.json();
  }

  function getCart() {
    return nativeFetch(route('cart.js'), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(asJSON);
  }

  function addGift(quantity) {
    var properties = {};
    properties[MARKER_KEY] = MARKER_VALUE;
    return nativeFetch(route('cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{ id: GIFT_ID, quantity: quantity, properties: properties }]
      })
    }).then(asJSON);
  }

  function setLineQuantity(key, quantity) {
    return nativeFetch(route('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(asJSON);
  }

  /* ---- Cart inspection -------------------------------------------- */

  function isGiftLine(item) {
    if (!item || item.variant_id !== GIFT_ID) {
      return false;
    }
    var props = item.properties;
    return !!(props && props[MARKER_KEY]);
  }

  // How many units on a line the discounts actually zeroed. A line can be
  // partly discounted — two of these promos have combinesWith.productDiscounts
  // set to false, so a cart holding two different triggers may only get one
  // unit free.
  function freeUnitsOn(item) {
    if (!item.quantity) {
      return 0;
    }
    var unitPrice = Math.round(item.original_line_price / item.quantity);
    if (unitPrice <= 0) {
      return item.quantity;
    }
    var discounted = item.original_line_price - item.final_line_price;
    return Math.min(item.quantity, Math.floor(discounted / unitPrice));
  }

  function readCart(cart) {
    var items = (cart && cart.items) || [];
    var state = {
      qualifyingQty: 0,
      paidQty: 0,
      giftLines: [],
      giftQty: 0,
      giftFreeQty: 0
    };

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (QUALIFYING.indexOf(item.variant_id) !== -1) {
        state.qualifyingQty += item.quantity;
      }
      if (item.variant_id === GIFT_ID) {
        if (isGiftLine(item)) {
          state.giftLines.push(item);
          state.giftQty += item.quantity;
          state.giftFreeQty += freeUnitsOn(item);
        } else {
          // The customer is paying for this one — off limits.
          state.paidQty += item.quantity;
        }
      }
    }
    return state;
  }

  // Identifies the cart shape a discount-capacity probe was measured against.
  // Only the trigger and paid-gift counts affect how many units go free, so a
  // cap learned under one signature stays valid until one of them moves.
  function cartSignature(state) {
    return state.qualifyingQty + ':' + state.paidQty;
  }

  function readCap(signature) {
    var raw = ssGet(SS_CAP);
    if (!raw) {
      return null;
    }
    try {
      var parsed = JSON.parse(raw);
      if (parsed && parsed.sig === signature && typeof parsed.cap === 'number') {
        return parsed.cap;
      }
    } catch (e) {
      /* corrupt value — treat as no cap */
    }
    return null;
  }

  function writeCap(signature, cap) {
    ssSet(SS_CAP, JSON.stringify({ sig: signature, cap: cap }));
  }

  /* ---- What the cart should look like ------------------------------ */

  function desiredGiftQty(state) {
    // No trigger left in the cart: drop the gift and start clean next time.
    if (state.qualifyingQty === 0) {
      ssSet(SS_OPTOUT, null);
      ssSet(SS_UNAVAILABLE, null);
      ssSet(SS_CAP, null);
      return 0;
    }

    // Nothing on the gift line went free, so the BxGy discount isn't winning
    // at all — another automatic discount can outrank it. Quietly charging
    // for something labelled free is worse than not offering it.
    if (state.giftQty > 0 && state.giftFreeQty === 0) {
      ssSet(SS_UNAVAILABLE, '1');
    }
    if (ssGet(SS_UNAVAILABLE) === '1') {
      return 0;
    }

    // A gift line that shrank without us asking means the customer took
    // it out. Respect that for the rest of the session.
    var expected = ssNum(SS_EXPECTED);
    if (expected !== null && state.giftQty < expected) {
      ssSet(SS_OPTOUT, '1');
    }

    if (ssGet(SS_OPTOUT) === '1') {
      // Leave whatever they've chosen to keep, but never add more.
      return state.giftQty;
    }

    // One free unit per qualifying unit, less any they're already buying
    // — the discount zeroes those first, so a second line would be noise.
    var desired = state.qualifyingQty - state.paidQty;
    if (desired < 0) {
      desired = 0;
    }

    // Honour the discount capacity measured for this exact cart shape, so a
    // cart that can only free one unit doesn't get re-inflated on every
    // interaction and trimmed straight back down.
    var cap = readCap(cartSignature(state));
    if (cap !== null && cap < desired) {
      desired = cap;
    }

    return Math.min(desired, MAX_GIFTS);
  }

  /* ---- Applying the difference ------------------------------------- */

  function applyOps(state, desired) {
    var ops = [];

    // Collapse any duplicate gift lines onto the first one. Shouldn't
    // happen, but a stale cart from an earlier build could carry them.
    for (var i = 1; i < state.giftLines.length; i++) {
      (function (key) {
        ops.push(function () { return setLineQuantity(key, 0); });
      }(state.giftLines[i].key));
    }

    var primary = state.giftLines[0] || null;
    if (!primary) {
      if (desired > 0) {
        ops.push(function () { return addGift(desired); });
      }
    } else if (primary.quantity !== desired) {
      ops.push(function () { return setLineQuantity(primary.key, desired); });
    }

    if (!ops.length) {
      return Promise.resolve(false);
    }

    // Cart mutations must run one at a time — Shopify rejects concurrent
    // writes to the same cart.
    return ops.reduce(function (chain, op) {
      return chain.then(op);
    }, Promise.resolve()).then(function () {
      return true;
    });
  }

  /* ---- Verification -------------------------------------------------- */

  // /cart/add.js reports a line before discount allocation settles, so a gift
  // we just added has to be re-read to see how much of it really went free.
  // Anything the discounts didn't cover gets trimmed off — the customer is
  // never left paying for a line the cart calls a gift.
  function trimGiftToFree() {
    return getCart().then(function (cart) {
      var state = readCart(cart);
      if (!state.giftQty || state.giftFreeQty >= state.giftQty) {
        return false;
      }

      var keep = state.giftFreeQty;
      writeCap(cartSignature(state), keep);
      if (keep <= 0) {
        ssSet(SS_UNAVAILABLE, '1');
      }
      ssSet(SS_EXPECTED, String(keep));

      var ops = [];
      for (var i = 1; i < state.giftLines.length; i++) {
        (function (key) {
          ops.push(function () { return setLineQuantity(key, 0); });
        }(state.giftLines[i].key));
      }
      var primary = state.giftLines[0];
      if (primary && primary.quantity !== keep) {
        ops.push(function () { return setLineQuantity(primary.key, keep); });
      }

      return ops.reduce(function (chain, op) {
        return chain.then(op);
      }, Promise.resolve()).then(function () { return true; });
    });
  }

  /* ---- UI refresh --------------------------------------------------- */

  function onCartPage() {
    return /\/cart\/?$/.test(window.location.pathname);
  }

  function reloadCartPage() {
    // The /cart page is server-rendered, so a change here needs a reload.
    // Capped so a persistently-failing reconcile can never loop forever.
    var count = ssNum(SS_RELOADS) || 0;
    if (count >= MAX_RELOADS) {
      return;
    }
    ssSet(SS_RELOADS, String(count + 1));
    window.location.reload();
  }

  function refreshUI() {
    if (window.ACGCartDrawer && typeof window.ACGCartDrawer.refresh === 'function') {
      window.ACGCartDrawer.refresh();
    }
    if (window.ACGAjaxCart && typeof window.ACGAjaxCart.refreshCartCount === 'function') {
      window.ACGAjaxCart.refreshCartCount();
    }
    document.dispatchEvent(new CustomEvent('acg:free-gift:updated'));
    if (onCartPage()) {
      reloadCartPage();
    }
  }

  /* ---- Reconcile ---------------------------------------------------- */

  function reconcile() {
    if (busy) {
      queued = true;
      return Promise.resolve();
    }
    busy = true;

    return getCart()
      .then(function (cart) {
        var state = readCart(cart);
        var desired = desiredGiftQty(state);
        return applyOps(state, desired).then(function (changed) {
          ssSet(SS_EXPECTED, String(desired));
          if (!changed) {
            // Settled state — let the cart page reload budget recover.
            ssSet(SS_RELOADS, '0');
            return;
          }
          var check = desired > 0 ? trimGiftToFree() : Promise.resolve(false);
          return check.then(refreshUI);
        });
      })
      .catch(function () {
        // A failed reconcile must never break the cart. The promo simply
        // doesn't apply this time; the next cart action retries.
      })
      .then(function () {
        busy = false;
        if (queued) {
          queued = false;
          schedule();
        }
      });
  }

  function schedule() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcile, DEBOUNCE_MS);
  }

  /* ---- Cart-mutation hooks ------------------------------------------ */

  function urlOf(input) {
    if (typeof input === 'string') {
      return input;
    }
    if (input && typeof input.url === 'string') {
      return input.url;
    }
    return '';
  }

  function installFetchHook() {
    var previous = window.fetch;
    window.fetch = function (input) {
      var watch = CART_MUTATION_RE.test(urlOf(input));
      var result = previous.apply(this, arguments);
      if (!watch) {
        return result;
      }
      return result.then(function (response) {
        if (response && response.ok) {
          schedule();
        }
        return response;
      });
    };
  }

  function installXHRHook() {
    if (!window.XMLHttpRequest || !window.XMLHttpRequest.prototype) {
      return;
    }
    var proto = window.XMLHttpRequest.prototype;
    var open = proto.open;
    var send = proto.send;

    proto.open = function (method, url) {
      try {
        this._acgWatchCart = CART_MUTATION_RE.test(String(url));
      } catch (e) {
        this._acgWatchCart = false;
      }
      return open.apply(this, arguments);
    };

    proto.send = function () {
      var xhr = this;
      if (xhr._acgWatchCart) {
        xhr.addEventListener('load', function () {
          if (xhr.status >= 200 && xhr.status < 300) {
            schedule();
          }
        });
      }
      return send.apply(this, arguments);
    };
  }

  /* ---- Init ---------------------------------------------------------- */

  installFetchHook();
  installXHRHook();

  document.addEventListener('acg:cart:added', schedule);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }

  window.ACGFreeGiftAuto = {
    initialised: true,
    giftVariantId: GIFT_ID,
    qualifyingVariantIds: QUALIFYING,
    markerKey: MARKER_KEY,
    reconcile: reconcile,
    // Used by the "claim your free gift" link when a customer changes
    // their mind after removing it.
    clearOptOut: function () {
      ssSet(SS_OPTOUT, null);
      ssSet(SS_EXPECTED, null);
      ssSet(SS_CAP, null);
      ssSet(SS_UNAVAILABLE, null);
      return reconcile();
    }
  };
}());
